import {
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    OnDestroy,
    AfterViewInit,
    ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
    MatDialogModule,
    MAT_DIALOG_DATA,
    MatDialogRef,
    MatDialog,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { AlertService } from 'app/mock-api/common/services/alert.service';
import { Subject, takeUntil, filter as rxFilter } from 'rxjs';
import { NgApexchartsModule } from 'ng-apexcharts';
import { DeleteConfirmationComponent } from 'app/ui/delete-confirmation/delete-confirmation.component';
import { ChatService } from 'app/company/ai-chat/agent-chat/chat.service';
import { ActivitiesService } from 'app/company/activities/activities.service';
import { DynamicChartsComponent } from 'app/ui/dynamic-charts/dynamic-charts.component';
import { FormattingPipe } from 'app/modules/shared/services/formatting-data.pipe';
import { CopyOnDblclickDirective } from '@fuse/directives/copy-on-dblclick.directive';
import { NewTrackerService } from 'app/company/tracker/new-tracker.service';
import { FilterNewTrackerComponent } from 'app/company/tracker/component/filter-new-tracker/filter-new-tracker.component';
import { TrackerFilterHelper, FilterPayload } from 'app/company/tracker/tracker-filter.helper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TruncatePipe } from 'app/core/pipes/truncate.pipe';
import { DataSourceActionsService } from 'app/company/data-sources/components/data-sources/data-source-actions.service';
import { TableService } from 'app/company/data-sources/tables.service';
import { AddTablesComponent } from 'app/company/data-sources/components/add-tables/add-tables.component';
import {
    externalDialogTypeFromDatatype,
    isExternalDatatype,
} from 'app/company/data-sources/data-source-table-route.util';

@Component({
    selector: 'app-full-chart-page',
    standalone: true,
    imports: [
        CommonModule,
        TranslocoModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        NgApexchartsModule,
        DynamicChartsComponent,
        CopyOnDblclickDirective,
        FormattingPipe,
        DecimalPipe,
        DatePipe,
        MatTooltipModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatFormFieldModule,
        MatProgressSpinnerModule,
        TruncatePipe,
    ],
    templateUrl: './full-chart-page.component.html',
    styleUrls: ['./full-chart-page.component.scss'],
    providers: [FormattingPipe, DecimalPipe, DatePipe],
})
export class FullChartPageComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    protected _unsubscribeAll = new Subject<void>();

    chartOptions: any = null;
    chartLoading = true;
    tableLoading = true;
    tableError: string | null = null;
    tableRows: any[] = [];
    tableColumns: string[] = [];
    appliedFilters: any[] = [];
    permanentFilters: any[] = [];
    trackerName: string | null = null;
    /** Data source object for Tracker dialog (parity with new-tracker card). */
    trackerDs: any = null;
    noTrackerData = false;
    private _lang = 'en';

    // Date range for Tracker
    startDate: Date | null = null;
    endDate: Date | null = null;
    showDatePicker = false;
    currentSortOrder: 'asc' | 'desc' | null = null;
    private _filtersInteracted = false;

    @ViewChild('trackerChart') trackerChartComponent?: DynamicChartsComponent;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data1: any,
        private _dialogRef: MatDialogRef<FullChartPageComponent>,
        private _dialog: MatDialog,
        private _alertService: AlertService,
        private _chatService: ChatService,
        private _activityService: ActivitiesService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _translocoService: TranslocoService,
        private trackerService: NewTrackerService,
        private _tableService: TableService,
        private _dataSourceActions: DataSourceActionsService
    ) {
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(lang => (this._lang = lang || 'en'));
        this._lang = this._translocoService.getActiveLang() || 'en';
    }

    ngOnInit() {
        if (this.data1?.from === 'Tracker') {
            this._dialogRef.disableClose = true;

            this._dialogRef.backdropClick()
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => this._closeWithState());

            this._dialogRef.keydownEvents()
                .pipe(
                    rxFilter((e: KeyboardEvent) => e.key === 'Escape'),
                    takeUntil(this._unsubscribeAll)
                )
                .subscribe(() => this._closeWithState());
        }
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.chartOptions = this.data1.chart;
            this.appliedFilters = this.data1?.appliedFilters?.length > 0
                ? this.data1.appliedFilters
                : this.chartOptions?.appliedFilters || [];
            this.permanentFilters = this.data1?.permanentFilters || [];
            this.trackerName = this.data1?.tracker_name || null;
            this.trackerDs = this.data1?.tracker_ds ?? null;
            this.currentSortOrder = this.data1?.sortOrder || null;
            this._filtersInteracted = this.data1?.filtersInteracted || false;

            // Initialize date range from data1
            if (this.data1?.start_date) {
                this.startDate = this._parseDateString(this.data1.start_date);
            }
            if (this.data1?.end_date) {
                this.endDate = this._parseDateString(this.data1.end_date);
            }

            // Force Angular to detect changes
            this._changeDetectorRef.detectChanges();

            // Fetch complete permanent filters (with values) from API
            this._fetchPermanentFilters();
        }, 0);
    }

    private _parseDateString(dateStr: string): Date | null {
        if (!dateStr) return null;
        const parts = dateStr.split('-').map(p => parseInt(p, 10));
        if (parts.length !== 3 || parts.some(n => isNaN(n))) return null;
        // dd-MM-yyyy (day <= 31) vs yyyy-MM-dd (first part > 31)
        const day = parts[0] <= 31 ? parts[0] : parts[2];
        const month = (parts[0] <= 31 ? parts[1] : parts[1]) - 1;
        const year = parts[0] <= 31 ? parts[2] : parts[0];
        const d = new Date(year, month, day);
        return isNaN(d.getTime()) ? null : d;
    }

    onDateRangeChange(e: {
        startDate: Date | null;
        endDate: Date | null;
    }): void {
        if (this.data1?.from !== 'Tracker') return;
        const trackerId = this.chartOptions?.trackerId || this.chartOptions?.id;
        if (!trackerId) return;

        const format = (d: Date | null) =>
            d
                ? `${String(d.getDate()).padStart(2, '0')}-${String(
                      d.getMonth() + 1
                  ).padStart(2, '0')}-${d.getFullYear()}`
                : undefined;

        const start = format(e.startDate) || undefined;
        const end = format(e.endDate) || undefined;

        this._refreshTrackerData(trackerId, start, end, this.appliedFilters);
    }

    openFilterDialog(): void {
        if (this.data1?.from !== 'Tracker') return;
        const trackerId = this.chartOptions?.trackerId || this.chartOptions?.id;
        if (!trackerId) return;

        this.trackerService
            .getTrackerDimensionsColumns(trackerId)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response: any) => {
                const defaultFilters = response.data.filters || [];
                const filtersForDialog = this._filtersInteracted
                    ? (this.appliedFilters || [])
                    : (this.appliedFilters?.length > 0 ? this.appliedFilters : defaultFilters);

                const dialogRef = this._dialog.open(FilterNewTrackerComponent, {
                    width: '50%',
                    maxHeight: '60dvh',
                    data: {
                        dimensionColumns: response.data.dimensionColumns || [],
                        filters: filtersForDialog,
                        table: response.data.table,
                        isExternal: response.data.isExternal,
                        dataSourceId: response.data.dataSourceId,
                    },
                });

                dialogRef
                    .afterClosed()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe((result) => {
                        if (result) {
                            this._filtersInteracted = true;
                            this.appliedFilters = TrackerFilterHelper.normalizeFilters(
                                result.selectedItems || []
                            ).map(f => ({ column: f.column, values: f.values }));
                            const start = this.data1?.start_date || undefined;
                            const end = this.data1?.end_date || undefined;
                            this._refreshTrackerData(
                                trackerId,
                                start,
                                end,
                                result.selectedItems || []
                            );
                        }
                    });
            });
    }

    resetFilters(): void {
        if (this.data1?.from !== 'Tracker') return;
        const trackerId = this.chartOptions?.trackerId || this.chartOptions?.id;
        if (!trackerId) return;

        this.appliedFilters = [];
        this._filtersInteracted = true;
        this.startDate = null;
        this.endDate = null;
        this._refreshTrackerData(trackerId, undefined, undefined, []);
    }

    toggleDatePicker(): void {
        this.showDatePicker = !this.showDatePicker;
        this._changeDetectorRef.markForCheck();
    }

    applyDateRange(): void {
        if (this.data1?.from !== 'Tracker') return;
        const trackerId = this.chartOptions?.trackerId || this.chartOptions?.id;
        if (!trackerId) return;

        const format = (d: Date | null) =>
            d
                ? `${String(d.getDate()).padStart(2, '0')}-${String(
                      d.getMonth() + 1
                  ).padStart(2, '0')}-${d.getFullYear()}`
                : undefined;

        const start = format(this.startDate) || undefined;
        const end = format(this.endDate) || undefined;

        this.showDatePicker = false;
        this._refreshTrackerData(trackerId, start, end, this.appliedFilters);
    }

    private _refreshTrackerData(
        trackerId: number | string,
        start?: string,
        end?: string,
        selectedItems?: any[]
    ): void {
        const modal = this._buildTrackerPayload(selectedItems);

        this.chartLoading = true;
        this.tableLoading = true;
        this.noTrackerData = false;
        this._changeDetectorRef.detectChanges();

        this.trackerService
            .getTrackerById(trackerId, start, end, modal)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response: any) => {
                    const updated = response?.trackers?.[0];
                    if (updated?.chart) {
                        this.noTrackerData = false;
                        this.chartOptions = {
                            ...this.chartOptions,
                            ...updated.chart,
                            trackerId: updated.id,
                            name: updated.name,
                            start_date: updated.start_date || null,
                            end_date: updated.end_date || null,
                            dateColumn: updated.dateColumn,
                            metricColumns: updated.metricColumns,
                            query_explanation: updated.query_explanation,
                            appliedFilters: this.appliedFilters,
                        };
                        this.data1.start_date = start;
                        this.data1.end_date = end;
                        this.chartLoading = false;
                        this._changeDetectorRef.detectChanges();
                        this._loadTrackerTableIfNeeded(start, end, modal);
                    } else {
                        this.noTrackerData = true;
                        this.chartLoading = false;
                        this.tableLoading = false;
                        this._changeDetectorRef.detectChanges();
                    }
                },
                error: () => {
                    this.noTrackerData = true;
                    this.chartLoading = false;
                    this.tableLoading = false;
                    this.tableError = 'Failed to update chart.';
                    this._changeDetectorRef.detectChanges();
                },
            });
    }

    private _fetchPermanentFilters(): void {
        if (this.data1?.from !== 'Tracker') return;
        const trackerId = this.chartOptions?.trackerId || this.chartOptions?.id;
        if (!trackerId) {
            this._loadTrackerTableIfNeeded();
            return;
        }

        this.trackerService
            .getTrackerDimensionsColumns(trackerId)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response: any) => {
                    this.permanentFilters = response.data?.filters || [];
                    this._changeDetectorRef.detectChanges();
                    const start = this.data1?.start_date || undefined;
                    const end = this.data1?.end_date || undefined;
                    this._refreshTrackerData(trackerId, start, end, this.appliedFilters);
                },
                error: () => {
                    const start = this.data1?.start_date || undefined;
                    const end = this.data1?.end_date || undefined;
                    this._refreshTrackerData(trackerId, start, end, this.appliedFilters);
                },
            });
    }

    private _buildTrackerPayload(selectedItems?: any[]): FilterPayload {
        const effectiveFilters = selectedItems ?? this.appliedFilters ?? [];
        return TrackerFilterHelper.buildPayload({
            hasUserInteracted: this._filtersInteracted,
            appliedFilters: effectiveFilters,
            permanentFilters: this.permanentFilters,
        });
    }

    onSortChange(order: 'asc' | 'desc' | null): void {
        this.currentSortOrder = order;
    }

    closeWithState(): void {
        this._closeWithState();
    }

    private _closeWithState(): void {
        const selectedSeriesList =
            this.data1?.from === 'Tracker' && this.trackerChartComponent?.selectedSeriesList?.length
                ? [...this.trackerChartComponent.selectedSeriesList]
                : undefined;
        this._dialogRef.close({
            appliedFilters: this.appliedFilters,
            permanentFilters: this.permanentFilters,
            filtersInteracted: this._filtersInteracted,
            startDate: this.startDate,
            endDate: this.endDate,
            sortOrder: this.currentSortOrder,
            selectedSeriesList,
        });
    }

    deleteChart(chartData) {
        const dialogRef = this._dialog.open(DeleteConfirmationComponent, {
            width: '30%',
            data: 'Chart',
            panelClass: 'cropperDialog',
        });
        dialogRef
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result) => {
                if (result) {
                    if (chartData.messageId) {
                        const modal = {
                            sessionId: chartData.sessionId,
                            messageId: chartData.messageId,
                            chartId: chartData.id,
                            favorite: false,
                        };
                        this._chatService
                            .markChartAsFavorite(modal)
                            .pipe(takeUntil(this._unsubscribeAll))
                            .subscribe(
                                (res) => {
                                    this._dialogRef.close(true);
                                },
                                (error) => {
                                    this._alertService.failedMessage(
                                        error.error.message
                                    );
                                }
                            );
                    } else if (chartData.questionId) {
                        const modal = {
                            chartId: chartData.id,
                            subActivityId: chartData.subActivityId,
                            favorite: false,
                        };
                        this._activityService
                            .markActivityChartAsFavorite(modal)
                            .pipe(takeUntil(this._unsubscribeAll))
                            .subscribe(
                                (res) => {
                                    this._dialogRef.close(true);
                                },
                                (error) => {
                                    this._alertService.failedMessage(
                                        error.error.message
                                    );
                                }
                            );
                    }
                }
            });
    }

    navigateToDataSource(ds: any): void {
        if (!ds) return;
        const eid = ds.eid ?? ds.id;
        if (eid == null) return;
        this._dataSourceActions.navigateToDetails({ ...ds, eid });
    }

    /**
     * Opens the same view-table dialog as new-tracker card (uses trackerName + trackerDs).
     */
    openViewTableDialog(): void {
        const tableName = this.trackerName;
        if (!tableName) return;
        const ds = this.trackerDs;
        const dataSourceId = ds?.eid ?? ds?.id;
        const datatype = ds?.datatype;

        const isExternal = isExternalDatatype(datatype);
        const dialogType = externalDialogTypeFromDatatype(datatype) ?? 'View';

        if (isExternal) {
            const dialogRef = this._dialog.open(AddTablesComponent, {
                width: '92%',
                maxHeight: '95dvh',
                panelClass: 'biggDialog',
                data: {
                    event: null,
                    tableName,
                    tableUserName: tableName,
                    dataSourceId,
                    tableContent: null,
                    tableInfo: {
                        value: tableName,
                        label: tableName,
                        about: '',
                        description: '',
                    },
                    isMyTable: false,
                    type: dialogType,
                },
            });
            dialogRef
                .afterClosed()
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => this._changeDetectorRef.markForCheck());
            return;
        }

        this._tableService
            .getTableByName(tableName, 0, 100)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (res: any) => {
                    const dialogRef = this._dialog.open(AddTablesComponent, {
                        width: '92%',
                        maxHeight: '95dvh',
                        panelClass: 'biggDialog',
                        data: {
                            event: null,
                            tableName,
                            tableUserName: tableName,
                            dataSourceId: dataSourceId ?? undefined,
                            tableContent: res?.data ?? null,
                            tableInfo: {
                                name: tableName,
                                userTableName: tableName,
                                about: res?.about ?? '',
                            },
                            isMyTable: false,
                            type: 'View',
                        },
                    });
                    dialogRef
                        .afterClosed()
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe(() =>
                            this._changeDetectorRef.markForCheck()
                        );
                },
                error: () => {
                    this._alertService.failedMessage(
                        'Failed to load table data.'
                    );
                    this._changeDetectorRef.markForCheck();
                },
            });
    }

    getDataSourceDisplayName(ds: any): string {
        if (!ds?.name) return '';
        return (
            ds.name || ds.name || ''
        );
    }

    exportAsCsv(): void {
        const exportData = Array.isArray(this.tableRows) ? this.tableRows : [];
        if (!exportData.length) return;

        const fileName =
            this.chartOptions?.name ||
            this.chartOptions?.tableName ||
            'DataTable';
        const csvContent = this._generateCsvContent(exportData);
        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;',
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${fileName}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    private _formatExportHeader(header: string): string {
        const withSpaces = header.replace(/[-_]+/g, ' ').trim();
        return withSpaces
            .toLowerCase()
            .split(/\s+/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    private _generateCsvContent(data: any[]): string {
        const rawKeys = Object.keys(data[0]);
        const headers = rawKeys
            .map(key => this._formatExportHeader(key))
            .join(',');
        const rows = data
            .map(row =>
                rawKeys
                    .map((key) => {
                        const value = row[key] ?? '';
                        const stringValue = String(value).replace(/"/g, '""');
                        return /[",\n]/.test(stringValue)
                            ? `"${stringValue}"`
                            : stringValue;
                    })
                    .join(',')
            )
            .join('\n');

        return `${headers}\n${rows}`;
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    private _loadTrackerTableIfNeeded(
        start?: string,
        end?: string,
        payload?: { selectedItems: any[] } | null
    ): void {
        try {
            if (this.data1?.from !== 'Tracker') return;
            const trackerId =
                this.chartOptions?.trackerId || this.chartOptions?.id;
            if (!trackerId) {
                this.tableLoading = false;
                this._changeDetectorRef.detectChanges();
                return;
            }

            this.tableLoading = true;
            this.tableError = null;
            const startParam =
                start ?? this.data1?.start_date ?? undefined;
            const endParam = end ?? this.data1?.end_date ?? undefined;
            const modal =
                payload ?? this._buildTrackerPayload(this.appliedFilters);

            this.trackerService
                .getTrackerData(String(trackerId), startParam, endParam, modal)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: (res: any) => {
                        const normalized = this._normalizeTableData(
                            res?.data ?? res
                        );
                        this.tableRows = normalized.rows;
                        this.tableColumns = normalized.columns;
                        this.tableLoading = false;
                        this._changeDetectorRef.detectChanges();
                    },
                    error: () => {
                        this.tableError = 'Failed to load data table.';
                        this.tableLoading = false;
                        this._changeDetectorRef.detectChanges();
                    },
                });
        } catch {
            this.tableError = 'Failed to prepare data table.';
            this.tableLoading = false;
            this._changeDetectorRef.detectChanges();
        }
    }

    private _normalizeTableData(payload: any): {
        rows: any[];
        columns: string[];
    } {
        // Handle shapes: { queries: [{ data: [...] }]} | { data: [...] } | [...]
        let rows: any[] = [];
        if (
            payload?.queries &&
            Array.isArray(payload.queries) &&
            payload.queries[0]?.data
        ) {
            rows = Array.isArray(payload.queries[0].data)
                ? payload.queries[0].data
                : [];
        } else if (Array.isArray(payload?.data)) {
            rows = payload.data;
        } else if (Array.isArray(payload)) {
            rows = payload;
        } else {
            rows = [];
        }

        const columns =
            rows.length > 0 && typeof rows[0] === 'object' && rows[0] !== null
                ? Object.keys(rows[0])
                : [];
        return { rows, columns };
    }
}
